import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
const apiUrl = import.meta.env.VITE_APP_API;
function AddBook() {
    const [categories, setCategories] = useState([])
    const [books, setBooks] = useState([])
    const [loading, setLoading] = useState(false)
    const [categoryLoading, setCategoryLoading] = useState(false)
    const [bookLoading, setBookLoading] = useState(false)
    const [showCategoryForm, setShowCategoryForm] = useState(false)
    const access_token = JSON.parse(localStorage.getItem("faceAuthTokenData"))
        ?.token.access;

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(10)

    // Book form state
    const [bookData, setBookData] = useState({
        name: '',
        category: '',
        author: '',
        publisher: '',
        total_amount: '',
        price: '',
        discount: '0.00',
        description: ''
    })
    const [editingBookId, setEditingBookId] = useState(null)
    const [selectedImage, setSelectedImage] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)

    // Category form state
    const [categoryData, setCategoryData] = useState({
        name: ''
    })

    // API base URL
    const API_BASE_URL = `${apiUrl}/api/bookstore/api`;

    // Fetch categories and books on component mount
    useEffect(() => {
        fetchCategories()
        fetchBooks(1)
    }, [])

    // Fetch all categories
    const fetchCategories = async () => {
        try {
            setCategoryLoading(true)
            const response = await axios.get(`${API_BASE_URL}/categories/`, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })
            console.log(response.data);

            setCategories(response.data.results)
        } catch (error) {
            console.error('Error fetching categories:', error)
            toast.error('Failed to fetch categories')
        } finally {
            setCategoryLoading(false)
        }
    }

    // Fetch books with pagination
    const fetchBooks = async (page = 1) => {
        try {
            setBookLoading(true)
            const response = await axios.get(`${API_BASE_URL}/books/?page=${page}&page_size=${pageSize}`, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })

            if (response.data.results) {
                setBooks(response.data.results)
                setTotalPages(Math.ceil(response.data.count / pageSize))
                setTotalCount(response.data.count)
                setCurrentPage(page)
            } else {
                setBooks(response.data)
                setTotalPages(1)
                setTotalCount(response.data.length)
                setCurrentPage(1)
            }
        } catch (error) {
            console.error('Error fetching books:', error)
            toast.error('Failed to fetch books')
        } finally {
            setBookLoading(false)
        }
    }

    // Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file')
                return
            }

            // Validate file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB')
                return
            }

            setSelectedImage(file)

            // Create preview
            const reader = new FileReader()
            reader.onload = (e) => {
                setImagePreview(e.target.result)
            }
            reader.readAsDataURL(file)
        }
    }

    // Remove selected image
    const removeImage = () => {
        setSelectedImage(null)
        setImagePreview(null)
    }

    // Create new category
    const handleCreateCategory = async (e) => {
        e.preventDefault()
        try {
            setCategoryLoading(true)
            const response = await axios.post(`${API_BASE_URL}/categories/`, categoryData, {
                headers: {
                    Authorization: `Bearer ${access_token}`
                }
            })
            toast.success('Category created successfully!')
            setCategoryData({ name: '' })
            setShowCategoryForm(false)
            fetchCategories() // Refresh categories list
        } catch (error) {
            console.error('Error creating category:', error)
            toast.error(error.response?.data?.name?.[0] || 'Failed to create category')
        } finally {
            setCategoryLoading(false)
        }
    }

    // Create or update book
    const handleCreateBook = async (e) => {
        e.preventDefault()

        // Validation
        if (!bookData.name || !bookData.category || !bookData.author || !bookData.publisher || !bookData.total_amount || !bookData.price) {
            toast.error('Please fill in all required fields')
            return
        }

        try {
            setLoading(true)

            // Create FormData for multipart/form-data
            const formData = new FormData()
            formData.append('name', bookData.name)
            formData.append('category', bookData.category)
            formData.append('author', bookData.author)
            formData.append('publisher', bookData.publisher)
            formData.append('total_amount', bookData.total_amount)
            formData.append('price', bookData.price)
            formData.append('discount', bookData.discount)
            formData.append('description', bookData.description)

            if (selectedImage) {
                formData.append('image', selectedImage)
            }

            if (editingBookId) {
                // Update existing book
                const response = await axios.put(`${API_BASE_URL}/books/${editingBookId}/`, formData, {
                    headers: {
                        Authorization: `Bearer ${access_token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                })
                toast.success('Book updated successfully!')
            } else {
                // Create new book
                const response = await axios.post(`${API_BASE_URL}/books/`, formData, {
                    headers: {
                        Authorization: `Bearer ${access_token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                })
                toast.success('Book created successfully!')
            }

            // Reset form
            setBookData({
                name: '',
                category: '',
                author: '',
                publisher: '',
                total_amount: '',
                price: '',
                discount: '0.00',
                description: ''
            })
            setSelectedImage(null)
            setImagePreview(null)
            setEditingBookId(null)

            // Refresh books list
            fetchBooks(currentPage)
        } catch (error) {
            console.error('Error saving book:', error)
            const errorMessage = error.response?.data?.name?.[0] ||
                error.response?.data?.category?.[0] ||
                'Failed to save book'
            toast.error(errorMessage)
        } finally {
            setLoading(false)
        }
    }

    // Handle input changes for book form
    const handleBookInputChange = (e) => {
        const { name, value } = e.target
        setBookData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // Handle input changes for category form
    const handleCategoryInputChange = (e) => {
        const { name, value } = e.target
        setCategoryData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    // Handle edit book
    const handleEditBook = (book) => {
        // Populate form with book data for editing
        setBookData({
            name: book.name,
            category: book.category,
            author: book.author,
            publisher: book.publisher,
            total_amount: book.total_amount.toString(),
            price: book.price.toString(),
            discount: book.discount.toString(),
            description: book.description || ''
        })

        // Set image preview if book has image
        if (book.image) {
            setImagePreview(`${apiUrl}${book.image}`)
            setSelectedImage(null) // Don't set selectedImage for existing images
        } else {
            setImagePreview(null)
            setSelectedImage(null)
        }

        setEditingBookId(book.id)

        // Scroll to form
        document.getElementById('book-form')?.scrollIntoView({ behavior: 'smooth' })
    }

    // Handle delete book
    const handleDeleteBook = async (bookId) => {
        if (window.confirm('Are you sure you want to delete this book?')) {
            try {
                await axios.delete(`${API_BASE_URL}/books/${bookId}/`, {
                    headers: {
                        Authorization: `Bearer ${access_token}`
                    }
                })
                toast.success('Book deleted successfully!')
                fetchBooks(currentPage) // Refresh books list
            } catch (error) {
                console.error('Error deleting book:', error)
                toast.error('Failed to delete book')
            }
        }
    }

    // Handle page change
    const handlePageChange = (page) => {
        fetchBooks(page)
    }

    // Generate pagination buttons
    const renderPagination = () => {
        const pages = []
        const maxVisiblePages = 5
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1)
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 mx-1 rounded-md ${i === currentPage
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                >
                    {i}
                </button>
            )
        }

        return pages
    }
    console.log(books);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className=" mx-auto">
                <h1 className="text-3xl font-bold text-gray-800 mb-8">Add Book</h1>

                {/* Category Management Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-700">Categories</h2>
                        <button
                            onClick={() => setShowCategoryForm(!showCategoryForm)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
                        >
                            {showCategoryForm ? 'Cancel' : 'Add New Category'}
                        </button>
                    </div>

                    {/* Category Form */}
                    {showCategoryForm && (
                        <form onSubmit={handleCreateCategory} className="mb-6 p-4 border border-gray-200 rounded-lg">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={categoryData.name}
                                        onChange={handleCategoryInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter category name"
                                        required
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button
                                        type="submit"
                                        disabled={categoryLoading}
                                        className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
                                    >
                                        {categoryLoading ? 'Creating...' : 'Create Category'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Categories List */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryLoading ? (
                            <div className="col-span-full text-center py-4">Loading categories...</div>
                        ) : categories.length > 0 ? (
                            categories?.map((category) => (
                                <div key={category.id} className="bg-gray-50 p-4 rounded-lg border">
                                    <h3 className="font-medium text-gray-800">{category.name}</h3>
                                    <p className="text-sm text-gray-500">Slug: {category.slug}</p>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center py-4 text-gray-500">
                                No categories found. Create your first category above.
                            </div>
                        )}
                    </div>
                </div>

                {/* Book Form Section */}
                <div id="book-form" className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-700">
                            {editingBookId ? 'Edit Book' : 'Add New Book'}
                        </h2>
                        {editingBookId && (
                            <button
                                onClick={() => {
                                    setEditingBookId(null)
                                    setBookData({
                                        name: '',
                                        category: '',
                                        author: '',
                                        publisher: '',
                                        total_amount: '',
                                        price: '',
                                        discount: '0.00',
                                        description: ''
                                    })
                                    setSelectedImage(null)
                                    setImagePreview(null)
                                }}
                                className="text-gray-500 hover:text-gray-700 text-sm"
                            >
                                Cancel Edit
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleCreateBook} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Book Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Book Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={bookData.name}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter book name"
                                    required
                                />
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category *
                                </label>
                                <select
                                    name="category"
                                    value={bookData.category}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {Array.isArray(categories) &&
                                        categories.map((category) => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}

                                </select>
                            </div>

                            {/* Author */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Author *
                                </label>
                                <input
                                    type="text"
                                    name="author"
                                    value={bookData.author}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter author name"
                                    required
                                />
                            </div>

                            {/* Publisher */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Publisher *
                                </label>
                                <input
                                    type="text"
                                    name="publisher"
                                    value={bookData.publisher}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter publisher name"
                                    required
                                />
                            </div>

                            {/* Total Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Total Amount *
                                </label>
                                <input
                                    type="number"
                                    name="total_amount"
                                    value={bookData.total_amount}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter total amount"
                                    min="0"
                                    required
                                />
                            </div>

                            {/* Price */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price *
                                </label>
                                <input
                                    type="number"
                                    name="price"
                                    value={bookData.price}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter price"
                                    step="0.01"
                                    min="0"
                                    required
                                />
                            </div>

                            {/* Discount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Discount
                                </label>
                                <input
                                    type="number"
                                    name="discount"
                                    value={bookData.discount}
                                    onChange={handleBookInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter discount amount"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </div>

                        {/* Image Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Book Cover Image
                            </label>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-4">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                    {imagePreview && (
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="px-3 py-2 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-600 mb-2">Preview:</p>
                                        <div className="relative inline-block">
                                            <img
                                                src={imagePreview}
                                                alt="Book cover preview"
                                                className="w-32 h-40 object-cover rounded-lg border border-gray-300"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={bookData.description}
                                onChange={handleBookInputChange}
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter book description"
                            />
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-8 py-3 rounded-md transition-colors font-medium"
                            >
                                {loading ? (editingBookId ? 'Updating...' : 'Creating...') : (editingBookId ? 'Update Book' : 'Create Book')}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Books List Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-700">Books List</h2>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-600">
                                Total: {totalCount} books
                            </span>
                            <button
                                onClick={() => fetchBooks(currentPage)}
                                disabled={bookLoading}
                                className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-md transition-colors"
                            >
                                {bookLoading ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    {bookLoading ? (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <p className="mt-2 text-gray-500">Loading books...</p>
                        </div>
                    ) : books.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Image
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Book Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Category
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Author
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Publisher
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Stock
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Price
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Discount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {books.map((book) => (
                                            <tr key={book.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {book.image ? (
                                                        <img
                                                            src={`${book.image}`}
                                                            alt={book.name}
                                                            className="w-12 h-16 object-cover rounded border"
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-16 bg-gray-200 rounded border flex items-center justify-center">
                                                            <span className="text-xs text-gray-500">No Image</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{book.name}</div>
                                                        <div className="text-sm text-gray-500">Slug: {book.slug}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                        {book.category_name || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {book.author}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {book.publisher}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${book.total_amount < 10
                                                        ? 'bg-red-100 text-red-800'
                                                        : book.total_amount < 20
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-green-100 text-green-800'
                                                        }`}>
                                                        {book.total_amount}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {parseFloat(book.price).toFixed(2)}tk
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {parseFloat(book.discount) > 0 ? `$${parseFloat(book.discount).toFixed(2)}` : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={() => handleEditBook(book)}
                                                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteBook(book.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="mt-6 flex items-center justify-between">
                                    <div className="text-sm text-gray-700">
                                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>

                                        {renderPagination()}

                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-gray-500 mb-4">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No books found</h3>
                            <p className="text-gray-500">Get started by creating your first book above.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AddBook